import { useRef, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { compressApi, type CompressFailure, type CompressOutcome } from '../features/compress/api.ts';

const MAX_BYTES = 25 * 1024 * 1024;

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isPdf(f: File): boolean {
  return f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
}

const ERROR_TEXT: Record<string, string> = {
  no_file: 'Choose a PDF to compress.',
  not_a_pdf: 'That file isn’t a valid PDF.',
  compress_failed: 'Compression failed. Please try again.',
};

export function CompressPdf(): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CompressOutcome | null>(null);
  const [dragOver, setDragOver] = useState(false);

  function pick(list: FileList | null | undefined): void {
    setError(null); setResult(null);
    const f = list?.[0];
    if (!f) return;
    if (!isPdf(f)) { setError('Only PDF files can be compressed.'); return; }
    if (f.size > MAX_BYTES) { setError(`${f.name} is over 25 MB.`); return; }
    setFile(f);
  }

  async function run(): Promise<void> {
    if (!file) return;
    setPending(true); setError(null); setResult(null);
    try {
      setResult(await compressApi.compress(file));
    } catch (err) {
      const f = err as CompressFailure;
      setError(ERROR_TEXT[f.code] ?? ERROR_TEXT.compress_failed!);
    } finally {
      setPending(false);
    }
  }

  const saved = result && result.originalSize > 0
    ? Math.max(0, Math.round((1 - result.compressedSize / result.originalSize) * 100))
    : 0;

  return (
    <div className="page-narrow space-y-8">
      <header className="space-y-2">
        <p className="text-sm font-medium text-muted-foreground">Tools</p>
        <h1 className="font-sans text-[2.25rem] md:text-[2.75rem] leading-none text-ink">
          Compress PDF
        </h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Shrink a PDF by downsampling its images (~150 DPI). Text stays crisp.
          Upload a file, compress, and download the smaller version.
        </p>
      </header>

      {/* Drop-zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Choose a PDF to compress"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); }
        }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files); }}
        className={[
          'relative flex flex-col items-center justify-center gap-4 rounded-3xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-container))] hover:bg-[hsl(var(--surface-container-high))]',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          className="sr-only"
          onChange={(e) => { pick(e.target.files); if (inputRef.current) inputRef.current.value = ''; }}
        />
        <div className="grid size-16 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]">
          <Icon name="cloud_upload" size={30} />
        </div>
        <p className="text-[1.05rem] font-medium text-ink">Drag &amp; drop a PDF here</p>
        <span className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] px-5 py-2.5 text-sm font-medium">
          <Icon name="folder_open" size={18} /> Browse files
        </span>
        <p className="text-xs text-muted-foreground">PDF only · up to 25 MB</p>
      </div>

      {/* Selected file */}
      {file && (
        <div className="flex items-center gap-3 rounded-2xl bg-[hsl(var(--surface-container-low))] p-4">
          <div className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))]">
            <Icon name="description" size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[0.9rem] font-medium text-ink truncate" title={file.name}>{file.name}</p>
            <p className="text-xs text-muted-foreground tabular">PDF · {fmtSize(file.size)}</p>
          </div>
          <button type="button" onClick={() => { setFile(null); setResult(null); setError(null); }}
            aria-label={`Remove ${file.name}`}
            className="shrink-0 inline-flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <Icon name="close" size={15} />
          </button>
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Result summary */}
      {result && (
        result.compressed ? (
          <div className="flex items-start gap-3 rounded-2xl bg-[hsl(var(--success-container))] p-4 text-[hsl(var(--on-success-container))]">
            <div className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--on-success-container)/0.12)] text-[hsl(var(--on-success-container))]">
              <Icon name="check_circle" size={20} />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm font-medium">Compressed and downloaded</p>
              <p className="text-sm">
                <span className="tabular font-medium">{fmtSize(result.originalSize)}</span>
                {' → '}<span className="tabular font-medium">{fmtSize(result.compressedSize)}</span>
              </p>
              <span className="pill pill-success">{saved}% smaller</span>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-[hsl(var(--surface-container-low))] p-4 text-sm text-muted-foreground">
            This PDF is already well-optimised — it couldn’t be made meaningfully smaller, so the original was downloaded unchanged.
          </div>
        )
      )}

      <div className="flex items-center justify-between gap-2 pt-2">
        <p className="text-sm text-muted-foreground">{file ? '1 file selected' : 'No file yet'}</p>
        <button type="button" onClick={() => void run()} disabled={!file || pending} className="btn-primary">
          {pending ? 'Compressing…' : (<><Icon name="compress" size={16} /> Compress &amp; download</>)}
        </button>
      </div>
    </div>
  );
}
